import clsx from 'clsx';
import type { AIFlag, VitalSigns } from '@/types/db';
import type { TimelineEventWithExtras } from '../lib/timelineQueries';
import { isMedActive } from '../lib/timelineQueries';
import { formatEventDate } from '../lib/formatDate';
import { formatDoctorName } from '../lib/formatDoctor';
import { getEventTypeMeta } from '../lib/eventType';
import {
  BlockIcon,
  InfoIcon,
  WarnIcon,
} from '@/components/icons/flag-icons';

interface Props {
  event: TimelineEventWithExtras;
  onClick: () => void;
  highlight?: boolean;
}

const FLAG_RANK = { block: 2, warn: 1, info: 0 } as const;
const FLAG_ICON = { block: BlockIcon, warn: WarnIcon, info: InfoIcon } as const;
const FLAG_SEVERITY_STYLE: Record<string, string> = {
  block: 'bg-red-50 text-flag-block border-flag-block/40',
  warn: 'bg-amber-50 text-flag-warn border-flag-warn/30',
  info: 'bg-blue-50 text-flag-info border-flag-info/20',
};

function CompactFlagSummary({ flags }: { flags: AIFlag[] }) {
  const active = flags.filter((f) => !f.resolved_at);
  if (active.length === 0) return null;
  const worst = active.reduce((a, b) =>
    FLAG_RANK[b.severity] > FLAG_RANK[a.severity] ? b : a,
  );
  const Icon = FLAG_ICON[worst.severity];
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        FLAG_SEVERITY_STYLE[worst.severity],
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {active.length === 1 ? '1 flag' : `${active.length} flags`}
    </span>
  );
}

function VitalsSummaryLine({ vitals }: { vitals: VitalSigns }) {
  const parts: string[] = [];
  if (vitals.heart_rate != null) parts.push(`HR ${vitals.heart_rate} bpm`);
  if (vitals.systolic_bp != null && vitals.diastolic_bp != null)
    parts.push(`BP ${vitals.systolic_bp}/${vitals.diastolic_bp}`);
  if (vitals.blood_sugar != null) parts.push(`Sugar ${vitals.blood_sugar} mg/dL`);
  if (parts.length === 0) return null;
  return (
    <p className="text-sm font-medium truncate mb-0.5">
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && <span className="text-ink-subtle mx-1">·</span>}
          <span className="text-ink-muted">{p.split(' ')[0]}</span>{' '}
          <span className="text-ink">{p.split(' ').slice(1).join(' ')}</span>
        </span>
      ))}
    </p>
  );
}

export function TimelineEventCard({ event, onClick, highlight }: Props) {
  const typeMeta = getEventTypeMeta(event.event_type);
  const medCount = event.medications.length;
  const today = new Date().toISOString().slice(0, 10);
  const hasActiveMed = event.medications.some((m) =>
    isMedActive(m, today, event.event_date),
  );
  const provider = event.doctor_name
    ? formatDoctorName(event.doctor_name)
    : event.clinic_name || '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'card w-full text-left p-4 active:bg-canvas transition-colors',
        highlight && 'ring-2 ring-accent',
      )}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className={clsx(
            'relative shrink-0 h-10 w-10 rounded-full flex items-center justify-center',
            typeMeta.iconBg,
            typeMeta.iconText,
            highlight && 'animate-pulse-ring',
          )}
        >
          <typeMeta.Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          {/* Row 1: type badge + date */}
          <div className="flex items-center justify-between mb-0.5">
            <span
              className={clsx(
                'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold tracking-tight',
                typeMeta.pillBg,
                typeMeta.pillText,
              )}
            >
              {typeMeta.label}
            </span>
            <p className="text-xs text-ink-muted shrink-0">
              {formatEventDate(event.event_date)}
            </p>
          </div>

          {/* Row 2: vitals summary or diagnosis */}
          {event.event_type === 'vitals' && event.vital_signs ? (
            <VitalsSummaryLine vitals={event.vital_signs} />
          ) : event.diagnosis ? (
            <p className="font-semibold truncate mb-0.5">{event.diagnosis}</p>
          ) : null}

          {/* Row 3: provider + medicine count + active badge */}
          {(provider || medCount > 0) && (
            <p className="text-sm text-ink-muted truncate">
              {provider}
              {medCount > 0 &&
                `${provider ? ' · ' : ''}${medCount} ${medCount === 1 ? 'medicine' : 'medicines'}`}
              {hasActiveMed && (
                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#E6F4EA] text-[#2E7D32] align-middle">
                  Active
                </span>
              )}
            </p>
          )}

          {/* Row 4: compact flag summary */}
          {event.ai_flags.length > 0 && (
            <div className="mt-1.5">
              <CompactFlagSummary flags={event.ai_flags} />
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
